import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Check, CheckCheck, Trash2, Sparkles, CalendarDays, Info, Menu, X as XIcon, Sun, Moon, Package, UserCog, Settings, BarChart3, FileDown, LogOut, ChevronDown, Target, Clock } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useNotificationWS } from "@/hooks/useNotificationWS";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  assignment: Sparkles,
  deadline: CalendarDays,
  info: Info,
};

const typeColors = {
  assignment: "text-purple-500",
  deadline: "text-amber-500",
  info: "text-blue-500",
};

export const AppHeader = ({ onMobileMenuToggle, mobileMenuOpen, onNavigate, onExport }) => {
  const { user, token, logout, isAdmin, isProductManager, selectedProduct } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data);
    } catch {}
    setLoading(false);
  }, []);

  // WebSocket: real-time unread count updates
  const handleWSMessage = useCallback((data) => {
    if (data.type === "unread_count") {
      setUnreadCount(data.count);
      // If dropdown is open, refresh notifications to show the new one
      if (data.latest) {
        setNotifications((prev) => {
          const exists = prev.some((n) => n.id === data.latest.id);
          if (exists) return prev;
          return [{ ...data.latest, is_read: false }, ...prev].slice(0, 50);
        });
      }
    }
  }, []);

  useNotificationWS(token, handleWSMessage);

  // Check deadlines on mount + fallback poll every 60s
  useEffect(() => {
    fetchUnreadCount();
    axios.post(`${API}/notifications/check-deadlines`).catch(() => {});
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (showDropdown) fetchNotifications();
  }, [showDropdown, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDropdown]);

  // Close profile menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    if (showProfileMenu) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showProfileMenu]);

  const handleProfileNavigate = (panel) => {
    setShowProfileMenu(false);
    if (onNavigate) onNavigate(panel);
  };

  const handleExport = () => {
    setShowProfileMenu(false);
    if (onExport) onExport();
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    logout();
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      const removed = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (removed && !removed.is_read) setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);
    if (notif.link_panel && onNavigate) {
      onNavigate(notif.link_panel);
      setShowDropdown(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-40 h-16 glass-header border-b border-slate-100 dark:border-slate-800 flex items-center justify-between px-6 shadow-sm transition-colors duration-300"
      data-testid="app-header"
    >
      {/* Left side: hamburger + logo */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMobileMenuToggle}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all btn-press"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <XIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <img 
          src={isDark ? "/usahs-logo-white-dark.png" : "/usahs-header-logo.png"} 
          alt="University of St. Augustine for Health Sciences" 
          className="h-10 sm:h-12 object-contain" 
          data-testid="header-logo" 
        />
      </div>

      {/* Right side: notification bell */}
      <div className="flex items-center gap-2">
        {/* Notification Bell */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="relative w-10 h-10 flex items-center justify-center rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all group btn-press"
            data-testid="notification-bell"
          >
            <Bell className="w-[18px] h-[18px] group-hover:scale-110 transition-transform" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white ring-2 ring-white dark:ring-slate-900 shadow-md animate-pulse"
                data-testid="notification-badge"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown */}
          {showDropdown && (
            <div
              className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 rounded-2xl shadow-2xl border overflow-hidden scale-in"
              style={{ 
                boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
                background: "var(--theme-dropdown-bg)",
                borderColor: "var(--theme-card-border)"
              }}
              data-testid="notification-dropdown"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--theme-card-border)", background: "var(--theme-hover-bg)" }}>
                <h3 className="text-sm font-semibold" style={{ color: "var(--theme-text-primary)" }}>Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs font-medium text-[#00D4C8] hover:text-teal-700 flex items-center gap-1 transition-colors btn-press"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                )}
              </div>

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto">
              {loading && notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-xs text-slate-400">Loading...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-10 text-center">
                  <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No notifications yet</p>
                  <p className="text-xs text-slate-300 mt-0.5">
                    You'll be notified when you're assigned tasks
                  </p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const Icon = typeIcons[notif.type] || Info;
                  const colorClass = typeColors[notif.type] || "text-slate-500";
                  let timeAgo = "";
                  try {
                    timeAgo = formatDistanceToNow(parseISO(notif.created_at), { addSuffix: true });
                  } catch {
                    timeAgo = "";
                  }

                  return (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 cursor-pointer transition-colors group ${
                        notif.is_read ? "bg-white" : "bg-purple-50/40"
                      } hover:bg-slate-50`}
                      onClick={() => handleNotificationClick(notif)}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        notif.is_read ? "bg-slate-100" : "bg-purple-100"
                      }`}>
                        <Icon className={`w-4 h-4 ${notif.is_read ? "text-slate-400" : colorClass}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-xs font-semibold truncate ${notif.is_read ? "text-slate-500" : "text-slate-800"}`}>
                            {notif.title}
                          </p>
                          {!notif.is_read && (
                            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${notif.is_read ? "text-slate-400" : "text-slate-600"}`}>
                          {notif.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {notif.product_name && (
                            <span className="text-[10px] font-medium text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                              {notif.product_name}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-400">{timeAgo}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        {!notif.is_read && (
                          <button
                            onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                            className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                          className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

        {/* User Profile Dropdown */}
        <div className="hidden sm:block h-8 w-px bg-slate-200 dark:bg-slate-700 mx-2" />
        
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="hidden sm:flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
            data-testid="header-profile-btn"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#00D4C8] to-[#39D98A] flex items-center justify-center text-white font-semibold text-sm shadow-sm">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-tight">{user?.name || "User"}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-tight">{user?.email || user?.username || ""}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showProfileMenu ? "rotate-180" : ""}`} />
          </button>

          {/* Profile Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-[#1E293B] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-600 overflow-hidden z-[100]" data-testid="header-profile-menu">
              <div className="py-1">
                <button onClick={() => handleProfileNavigate('myproducts')} data-testid="nav-myproducts"
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                  <Package className="w-4 h-4" /> <span>My Products</span>
                </button>
                <button onClick={() => handleProfileNavigate('settings')} data-testid="nav-profile-settings"
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                  <UserCog className="w-4 h-4" /> <span>Profile Settings</span>
                </button>

                {isAdmin && (
                  <button onClick={() => handleProfileNavigate('admin')} data-testid="nav-admin"
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                    <Settings className="w-4 h-4" /> <span>Admin Panel</span>
                  </button>
                )}

                {isAdmin && (
                  <button onClick={() => handleProfileNavigate('reports')} data-testid="nav-reports"
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                    <FileDown className="w-4 h-4" /> <span>Reports</span>
                  </button>
                )}

                {isAdmin && (
                  <button onClick={() => handleProfileNavigate('changelog')} data-testid="nav-changelog"
                    className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                    <Clock className="w-4 h-4" /> <span>Change Log</span>
                  </button>
                )}

                {(isAdmin || isProductManager) && selectedProduct && (
                  <>
                    <div className="h-px bg-slate-200 dark:bg-slate-600 mx-3 my-1" />
                    <button onClick={() => handleProfileNavigate('quadchart')} data-testid="nav-quadchart"
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                      <BarChart3 className="w-4 h-4" /> <span>Quad Chart</span>
                    </button>
                    <button onClick={() => handleProfileNavigate('goals')} data-testid="nav-goals"
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                      <Target className="w-4 h-4" /> <span>Goals</span>
                    </button>
                    <button onClick={handleExport} data-testid="nav-export-prd"
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-900 dark:hover:text-white transition-colors">
                      <FileDown className="w-4 h-4" /> <span>Export PRD</span>
                    </button>
                  </>
                )}

                <div className="h-px bg-slate-200 dark:bg-slate-600 mx-3 my-1" />
                <button onClick={handleLogout} data-testid="logout-btn"
                  className="flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-300 transition-colors">
                  <LogOut className="w-4 h-4" /> <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile User Avatar */}
        <button
          onClick={() => setShowProfileMenu(!showProfileMenu)}
          className="sm:hidden w-9 h-9 rounded-xl bg-gradient-to-br from-[#00D4C8] to-[#39D98A] flex items-center justify-center text-white font-semibold text-sm shadow-sm"
          data-testid="mobile-user-avatar"
        >
          {user?.name?.charAt(0)?.toUpperCase() || "U"}
        </button>
    </div>
    </header>
  );
};
